import React, { useEffect, useMemo } from 'react';
import { useField } from 'formik';
import { SelectField, MultiSelectField } from '../';
import { Project } from '../../models';
import { RootState, useAppSelector, useAppDispatch, loadProjects } from '../../redux';

interface ProjectSelectFieldProps {
    name: string;
    multiple?: boolean;
    placeholder?: string;
    className?: string;
    filter?: (project: Project) => boolean;
};

const projectIdOf = (value: AnyObj): string | null => {
    if (!value)
        return null;
    if (typeof value === 'string')
        return value;
    return value._id || null;
};

const ProjectSelectField = (props: ProjectSelectFieldProps) => {
    const { name, multiple, placeholder, className, filter } = props;

    const dispatch = useAppDispatch();
    const [, meta, helpers] = useField(name);
    const { setValue } = helpers;

    let projects = useAppSelector((state: RootState) => state.projects.projects);

    if (filter)
        projects = projects?.filter(filter);
    else
        projects = projects?.filter((project: Project) => project.active !== false);

    useEffect(() => {
        dispatch(loadProjects());
    }, [dispatch]);

    // MultiSelect expects string ids; normalize populated project objects from the API.
    useEffect(() => {
        if (!multiple)
            return;
        const raw = meta.value;
        if (!Array.isArray(raw) || raw.length === 0)
            return;
        if (raw.every((v: AnyObj) => typeof v === 'string'))
            return;
        const ids = raw.map(projectIdOf).filter(Boolean);
        setValue(ids);
    }, [multiple, meta.value, setValue]);

    const options = useMemo(() => (projects || []).map((project: Project) => ({
        value: project._id,
        label: project.name
    })).sort((a: AnyObj, b: AnyObj) => a.label > b.label ? 1 : -1), [projects]);

    if (multiple)
        return (
            <MultiSelectField
                name={name}
                options={options}
                placeholder={placeholder}
                className={className}
            />
        );

    return (
        <SelectField
            name={name}
            options={options}
            placeholder={placeholder}
            className={className}
        />
    );
};

export default ProjectSelectField;
